import { useState, useEffect } from 'react'

import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid2,
  TextField,
  Stack,
} from '@mui/material'
import { useTranslation } from 'react-i18next'

import { useProject } from '../../api/project'
import { useCreateInterestSignup } from '../../api/signup'
import { useSignup } from '../../context/signup'
import { useSnackbar } from '../../context/snackbar'
import { useProjectId } from '../../hooks/useProjectId'
import {
  isValidEmail,
  isValidFirstName,
  isValidLastName,
  isValidOrganizationName,
  isValidPhone,
  isValidZip,
  isValidBirthYear,
} from '../../validation'
import { EmailField as EmailFieldComponent } from '../EmailField'
import { HtmlConnected as Html } from '../html'
import * as helper from './helper'

const isValid = (prop) => {
  const [name, value] = Object.entries(prop)[0]
  switch (name) {
    case 'firstName':
      return isValidFirstName(value)
    case 'lastName':
      return isValidLastName(value)
    case 'email':
      return isValidEmail(value)
    case 'organizationName':
      return isValidOrganizationName(value)
    case 'phone':
      return isValidPhone(value)
    case 'zip':
      return isValidZip(value)
    case 'birthYear':
      return isValidBirthYear(value)
    case 'acceptPrivacyPolicy':
      return !!value
    default:
      return true
  }
}

const isCompanyValid = ({
  firstName,
  lastName,
  organizationName,
  email,
  phone,
  street,
  city,
  acceptPrivacyPolicy,
}) => {
  return (
    isValid({ firstName }) &&
    isValid({ lastName }) &&
    isValid({ organizationName }) &&
    isValid({ email }) &&
    isValid({ phone }) &&
    isValid({ street }) &&
    isValid({ city }) &&
    isValid({ acceptPrivacyPolicy })
  )
}

const isUserValid = ({
  firstName,
  lastName,
  birthYear,
  email,
  phone,
  street,
  zip,
  city,
  acceptPrivacyPolicy,
}) => {
  return (
    isValid({ firstName }) &&
    isValid({ lastName }) &&
    isValid({ birthYear }) &&
    isValid({ email }) &&
    isValid({ phone }) &&
    isValid({ street }) &&
    isValid({ zip }) &&
    isValid({ city }) &&
    isValid({ acceptPrivacyPolicy })
  )
}

export const InterestSignupForm = ({ user = {} }) => {
  const { t } = useTranslation()
  const { mergeUser, nextPage } = useSignup()
  const { showMessage } = useSnackbar()
  const { data: project } = useProject(useProjectId())
  const facebookPixelId = project?.facebookPixelId
  const { mutate: createInterestSignup, isPending: loading } =
    useCreateInterestSignup()
  const [state, setState] = useState(user)
  const [location, setLocation] = useState(undefined)
  const [referrer, setReferrer] = useState(undefined)
  const [touched, setTouched] = useState({})

  useEffect(() => {
    // Using helper to enable stubbing in test
    setLocation(helper.getWindowLocationHref())
  }, [])

  useEffect(() => {
    // Using helper to enable stubbing in test
    setReferrer(helper.getDocumentReferrer())
  }, [])

  const defaultBuyer = project?.allowedBuyers?.[0] || 'PRIVATE'

  const [isOrganization, setOrganization] = useState(defaultBuyer !== 'PRIVATE')

  function handleChange(event) {
    const { name, value } = event.target
    setState({ ...state, [name]: value })
  }

  function handleCheck(event) {
    const { name, checked } = event.target
    setState({ ...state, [name]: checked })
  }

  function handleBlur(event) {
    const { name } = event.target
    setTouched({ ...touched, [name]: true })
  }

  function handleCompanySubmit(event) {
    event.preventDefault()
    if (loading) {
      return
    }

    if (!isCompanyValid(state)) {
      return setTouched({
        firstName: true,
        lastName: true,
        organizationName: true,
        email: true,
        phone: true,
        street: true,
        zip: true,
        city: true,
        acceptPrivacyPolicy: true,
      })
    }

    delete state.birthYear

    createInterestSignup(
      { projectId: project.id, user: { ...state, location, referrer } },
      {
        onSuccess: (data) => {
          mergeUser({ ...data, signupType: 'INTEREST' })
          nextPage()
        },
        onError: (error) => showMessage(error.message),
      }
    )
  }

  function handleUserSubmit(event) {
    event.preventDefault()
    if (loading) {
      return
    }

    if (!isUserValid(state)) {
      return setTouched({
        firstName: true,
        lastName: true,
        birthYear: true,
        email: true,
        phone: true,
        street: true,
        zip: true,
        city: true,
        acceptPrivacyPolicy: true,
      })
    }

    delete state.organizationName

    createInterestSignup(
      { projectId: project.id, user: { ...state, location, referrer } },
      {
        onSuccess: (data) => {
          mergeUser({ ...data, signupType: 'INTEREST' })
          nextPage()
        },
        onError: (error) => showMessage(error.message),
      }
    )
  }

  return (
    <Stack
      spacing={2}
      component="form"
      onSubmit={isOrganization ? handleCompanySubmit : handleUserSubmit}
    >
      <Html name="personal-details" />
      {project?.allowedBuyers?.length > 1 && (
        <FormControlLabel
          label={
            defaultBuyer === 'PRIVATE'
              ? t('I represent an organization')
              : t("I'm an individual")
          }
          value={defaultBuyer === 'PRIVATE' ? isOrganization : !isOrganization}
          onChange={(e) =>
            setOrganization(
              defaultBuyer === 'PRIVATE' ? e.target.checked : !e.target.checked
            )
          }
          control={
            <Checkbox
              name="privateOrCompany"
              checked={
                defaultBuyer === 'PRIVATE' ? isOrganization : !isOrganization
              }
            />
          }
        />
      )}
      <Grid2 container spacing={2}>
        {isOrganization && (
          <>
            <Grid2
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <TextField
                fullWidth
                variant="outlined"
                onChange={handleChange}
                onBlur={handleBlur}
                label={t('Organization name')}
                name="organizationName"
                autoComplete="organization"
                value={state?.organizationName || ''}
                error={
                  touched.organizationName &&
                  !isValid({ organizationName: state?.organizationName })
                }
                helperText={
                  touched.organizationName &&
                  !isValid({ organizationName: state?.organizationName }) &&
                  t('Enter organization name')
                }
              />
            </Grid2>
          </>
        )}
        <Grid2
          size={{
            xs: 12,
            sm: 6,
          }}
        >
          <TextField
            fullWidth
            variant="outlined"
            onChange={handleChange}
            onBlur={handleBlur}
            label={
              isOrganization ? t('Contact person first name') : t('First name')
            }
            name="firstName"
            autoComplete="given-name"
            value={state?.firstName || ''}
            error={
              touched.firstName && !isValid({ firstName: state?.firstName })
            }
            helperText={
              touched.firstName &&
              !isValid({ firstName: state?.firstName }) &&
              t('Enter first name')
            }
          />
        </Grid2>
        <Grid2
          size={{
            xs: 12,
            sm: 6,
          }}
        >
          <TextField
            fullWidth
            variant="outlined"
            onChange={handleChange}
            onBlur={handleBlur}
            label={
              isOrganization ? t('Contact person last name') : t('Last name')
            }
            name="lastName"
            autoComplete="family-name"
            value={state?.lastName || ''}
            error={touched.lastName && !isValid({ lastName: state?.lastName })}
            helperText={
              touched.lastName &&
              !isValid({ lastName: state?.lastName }) &&
              t('Enter last name')
            }
          />
        </Grid2>
        <Grid2
          size={{
            xs: 12,
            sm: 6,
          }}
        >
          <EmailFieldComponent
            onChange={handleChange}
            onBlur={handleBlur}
            email={state?.email}
            touched={touched.email}
          />
        </Grid2>
        <Grid2
          size={{
            xs: 12,
            sm: 6,
          }}
        >
          <TextField
            fullWidth
            variant="outlined"
            onChange={handleChange}
            onBlur={handleBlur}
            label={t('Phone number')}
            name="phone"
            autoComplete="phone"
            type="tel"
            value={state?.phone || ''}
            error={touched.phone && !isValid({ phone: state?.phone })}
            helperText={
              touched.phone &&
              !isValid({ phone: state?.phone }) &&
              t('Enter phone number')
            }
          />
        </Grid2>
        <Grid2
          size={{
            xs: 12,
            sm: isOrganization ? 6 : 3,
          }}
        >
          <TextField
            fullWidth
            variant="outlined"
            onChange={handleChange}
            onBlur={handleBlur}
            label={t('Postal code')}
            name="zip"
            type="tel"
            value={state?.zip || ''}
            error={
              !isOrganization && touched.zip && !isValid({ zip: state?.zip })
            }
            helperText={
              !isOrganization &&
              touched.zip &&
              !isValid({ zip: state?.zip }) &&
              t('Enter postal code')
            }
          />
        </Grid2>
        {!isOrganization && (
          <Grid2
            size={{
              xs: 12,
              sm: 3,
            }}
          >
            <TextField
              fullWidth
              variant="outlined"
              onChange={handleChange}
              onBlur={handleBlur}
              label={t('Year of birth')}
              placeholder={t('yyyy')}
              name="birthYear"
              type="tel"
              value={state?.birthYear || ''}
              error={
                touched.birthYear && !isValid({ birthYear: state?.birthYear })
              }
              helperText={
                touched.birthYear && !isValid({ birthYear: state?.birthYear })
                  ? t('Enter year of birth (yyyy)')
                  : t('Optional')
              }
            />
          </Grid2>
        )}
      </Grid2>
      {project?.membership && (
        <Stack>
          <FormControlLabel
            name="membership"
            control={
              <Checkbox
                name="membership"
                checked={state?.membership ? true : false}
                onChange={handleCheck}
              />
            }
            label={<Html name="membership" />}
          />
        </Stack>
      )}
      <Stack>
        {facebookPixelId && (
          <FormControl component="fieldset">
            <FormControlLabel
              control={
                <Checkbox
                  name="acceptTracking"
                  checked={state?.acceptTracking ? true : false}
                  onChange={handleCheck}
                />
              }
              label={t('I accept tracking for marketing purposes (optional)')}
            />
          </FormControl>
        )}
        <FormControl
          required
          error={
            touched.acceptPrivacyPolicy &&
            !isValid({ acceptPrivacyPolicy: state?.acceptPrivacyPolicy })
          }
          component="fieldset"
        >
          <FormControlLabel
            control={
              <Checkbox
                name="acceptPrivacyPolicy"
                checked={state?.acceptPrivacyPolicy ? true : false}
                onChange={handleCheck}
                onBlur={handleBlur}
              />
            }
            label={<Html name="privacy-policy-checkbox-label" />}
          />
          {touched.acceptPrivacyPolicy &&
            !isValid({
              acceptPrivacyPolicy: state?.acceptPrivacyPolicy,
            }) && (
              <FormHelperText>
                {t('Please accept our personal data policy')}
              </FormHelperText>
            )}
        </FormControl>
      </Stack>
      <Box>
        <Button
          variant="contained"
          type="submit"
          loading={loading}
          onSubmit={isOrganization ? handleCompanySubmit : handleUserSubmit}
        >
          {t('Continue')}
        </Button>
      </Box>
    </Stack>
  )
}
